import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';

/**
 * Единицы транспорта (контейнеры/вагоны) — переиспользуемые редакторы.
 *
 * Модель: «позиция» (тип × количество) разворачивается в отдельные единицы со
 * стабильным `id` (генерируем на клиенте — нужно для распределения груза до
 * сохранения). Номер опционален — вписывается по мере поступления, в т.ч. уже
 * на этапе перевозки. Распределение груза ссылается на `id`, а не на номер.
 */

/** Принадлежность единицы транспорта. '' — не указана. */
export type Ownership = '' | 'SOC' | 'COC' | 'OWNED' | 'INVENTORY';

/** Вид единицы транспорта. */
export type UnitKind = 'CONTAINER' | 'WAGON';

/** Единица транспорта в форме. id стабилен (для ссылок cargoGroups). */
export interface TransportUnitForm {
  id: string;
  /** Контейнер или вагон — в одном поручении могут быть и те, и другие. */
  kind: UnitKind;
  type: string;
  number: string;
  /** Принадлежность (контейнер SOC/COC, вагон OWNED/INVENTORY); '' — не указана. */
  ownership: Ownership;
}

/** Названия вида единицы: для заголовков и подписей. */
export const KIND_WORD: Record<UnitKind, { one: string; many: string; inMany: string }> = {
  CONTAINER: { one: 'контейнер', many: 'контейнеры', inMany: 'контейнерах' },
  WAGON: { one: 'вагон', many: 'вагоны', inMany: 'вагонах' },
};

/** Опции типа и принадлежности — свои для контейнеров и вагонов. */
export const typeOptionsFor = (kind: UnitKind) =>
  kind === 'WAGON' ? WAGON_TYPE_OPTIONS : CONTAINER_TYPE_OPTIONS;
export const ownershipOptionsFor = (kind: UnitKind) =>
  kind === 'WAGON' ? WAGON_OWNERSHIP_OPTIONS : CONTAINER_OWNERSHIP_OPTIONS;

/**
 * Слово для набора единиц: «контейнерах» / «вагонах», а для смешанного
 * состава — нейтральное «единицах транспорта».
 */
export function unitsWord(units: TransportUnitForm[]): { one: string; inMany: string } {
  const kinds = new Set(units.map((u) => u.kind));
  if (kinds.size === 1) {
    const k = [...kinds][0];
    return { one: KIND_WORD[k].one, inMany: KIND_WORD[k].inMany };
  }
  return { one: 'единица транспорта', inMany: 'единицах транспорта' };
}

/** Тип отправки для поручения: «Контейнер», «Вагон» или оба через запятую. */
export function shipmentTypeFromUnits(units: TransportUnitForm[]): string {
  const has = (k: UnitKind) => units.some((u) => u.kind === k);
  const parts = [has('CONTAINER') ? 'Контейнер' : '', has('WAGON') ? 'Вагон' : ''].filter(Boolean);
  return parts.join(', ');
}

export const CONTAINER_TYPE_OPTIONS = [
  { value: '20', label: "20' (Standard)" },
  { value: '40', label: "40' (Standard)" },
  { value: '40HC', label: "40' High Cube" },
  { value: '45', label: "45'" },
];

/** Род вагона. value === label (хранится как русское наименование). */
export const WAGON_TYPE_OPTIONS = [
  { value: 'Крытый', label: 'Крытый' },
  { value: 'Платформа', label: 'Платформа' },
  { value: 'Полувагон', label: 'Полувагон' },
  { value: 'Цистерна', label: 'Цистерна' },
  { value: 'Рефрижератор', label: 'Рефрижератор' },
];

/** Принадлежность контейнера — варианты для Select. */
export const CONTAINER_OWNERSHIP_OPTIONS = [
  { value: 'SOC', label: 'SOC — клиента' },
  { value: 'COC', label: 'COC — перевозчика' },
];

/** Принадлежность вагона — варианты для Select. */
export const WAGON_OWNERSHIP_OPTIONS = [
  { value: 'OWNED', label: 'Собственный' },
  { value: 'INVENTORY', label: 'Инвентарный' },
];

/** Короткие подписи принадлежности (для бейджей/ярлыков/сводок). */
const OWNERSHIP_SHORT: Record<string, string> = {
  SOC: 'SOC',
  COC: 'COC',
  OWNED: 'собств.',
  INVENTORY: 'инвент.',
};

/** Короткая подпись принадлежности (для бейджей/списков). */
export function ownershipShort(o: Ownership | null | undefined): string {
  return o ? (OWNERSHIP_SHORT[o] ?? '') : '';
}

const TYPE_SHORT: Record<string, string> = {
  '20': "20'",
  '40': "40'",
  '40HC': "40'HC",
  '45': "45'",
};
const typeShort = (t: string) => TYPE_SHORT[t] ?? t;

function newId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `tmp-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/** Одна новая единица заданного вида и типа без номера. */
export function makeUnit(
  kind: UnitKind = 'CONTAINER',
  type = '20',
  ownership: Ownership = '',
): TransportUnitForm {
  return { id: newId(), kind, type, number: '', ownership };
}

/** Развернуть позицию «тип × количество» в массив единиц без номеров. */
export function expandPosition(
  kind: UnitKind,
  type: string,
  count: number,
  ownership: Ownership = '',
): TransportUnitForm[] {
  return Array.from({ length: Math.max(0, count) }, () => makeUnit(kind, type, ownership));
}

/** Порядковый номер единицы внутри её типа (1-based) — для плейсхолдера «20' #3». */
function seqWithinType(units: TransportUnitForm[], index: number): number {
  const type = units[index].type;
  let n = 0;
  for (let i = 0; i <= index; i++) if (units[i].type === type) n++;
  return n;
}

/** Ярлык единицы: номер, если есть, иначе «20' #3». Принадлежность — суффиксом. */
export function unitLabel(units: TransportUnitForm[], index: number): string {
  const u = units[index];
  const own = ownershipShort(u.ownership);
  const suffix = own ? ` · ${own}` : '';
  if (u.number?.trim()) return `${u.number.trim()} · ${typeShort(u.type)}${suffix}`;
  return `${typeShort(u.type)} #${seqWithinType(units, index)}${suffix}`;
}

/** Опции для MultiSelect распределения груза (value = id единицы). */
export function unitOptions(units: TransportUnitForm[]): { value: string; label: string }[] {
  return units.map((u, i) => ({ value: u.id, label: unitLabel(units, i) }));
}

// =====================================================================
// PositionsEditor — ввод позициями (тип × количество) + номера
// =====================================================================

/**
 * Редактор позиций: контейнеры и вагоны в одном списке.
 *
 * Добавление — кнопкой («Добавить контейнеры» / «Добавить вагоны»), которая
 * открывает окно с типом, количеством и принадлежностью. Добавленная позиция
 * становится строкой-группой: её можно развернуть и вписать номера.
 */
export function PositionsEditor({
  value,
  onChange,
  kinds = ['CONTAINER', 'WAGON'],
}: {
  value: TransportUnitForm[];
  onChange: (next: TransportUnitForm[]) => void;
  /** Какие виды единиц можно добавлять (кнопки). */
  kinds?: UnitKind[];
}) {
  /** Открытое окно добавления: для какого вида единиц. */
  const [addFor, setAddFor] = useState<UnitKind | null>(null);
  const [addType, setAddType] = useState('');
  const [addCount, setAddCount] = useState<number | string>(1);
  const [addOwnership, setAddOwnership] = useState<Ownership>('');

  const openAdd = (kind: UnitKind) => {
    setAddFor(kind);
    setAddType(typeOptionsFor(kind)[0]?.value ?? '');
    setAddCount(1);
    setAddOwnership('');
  };

  const confirmAdd = () => {
    if (!addFor) return;
    const count = Number(addCount) || 0;
    if (count <= 0 || !addType) return;
    onChange([...value, ...expandPosition(addFor, addType, count, addOwnership)]);
    setAddFor(null);
  };

  // Повторяющиеся номера — отмечаем.
  const numberCounts = value.reduce<Record<string, number>>((m, u) => {
    const n = u.number?.trim();
    if (n) m[n] = (m[n] || 0) + 1;
    return m;
  }, {});

  // Группы «вид + тип» в порядке появления.
  const groupKey = (u: TransportUnitForm) => `${u.kind}|${u.type}`;
  const groupsInOrder: { kind: UnitKind; type: string }[] = [];
  const seen = new Set<string>();
  for (const u of value) {
    const k = groupKey(u);
    if (!seen.has(k)) {
      seen.add(k);
      groupsInOrder.push({ kind: u.kind, type: u.type });
    }
  }

  const setNumber = (id: string, number: string) =>
    onChange(value.map((u) => (u.id === id ? { ...u, number } : u)));

  const setOwnership = (id: string, ownership: Ownership) =>
    onChange(value.map((u) => (u.id === id ? { ...u, ownership } : u)));

  const inGroup = (u: TransportUnitForm, g: { kind: UnitKind; type: string }) =>
    u.kind === g.kind && u.type === g.type;

  const setGroupOwnership = (g: { kind: UnitKind; type: string }, ownership: Ownership) =>
    onChange(value.map((u) => (inGroup(u, g) ? { ...u, ownership } : u)));

  const removeUnit = (id: string) => onChange(value.filter((u) => u.id !== id));

  const setGroupCount = (g: { kind: UnitKind; type: string }, nextCount: number) => {
    const ofGroup = value.filter((u) => inGroup(u, g));
    const cur = ofGroup.length;
    if (nextCount === cur) return;
    if (nextCount > cur) {
      // Новые единицы наследуют принадлежность позиции, если она одна на всех.
      const owns = new Set(ofGroup.map((u) => u.ownership));
      const ownership = owns.size === 1 ? [...owns][0] : '';
      onChange([...value, ...expandPosition(g.kind, g.type, nextCount - cur, ownership)]);
    } else {
      // Удаляем лишние, в первую очередь — без номера, с конца.
      const toRemove = cur - nextCount;
      const removable = [...ofGroup]
        .sort((a, b) => Number(Boolean(a.number?.trim())) - Number(Boolean(b.number?.trim())))
        .slice(0, toRemove)
        .map((u) => u.id);
      const removeSet = new Set(removable);
      onChange(value.filter((u) => !removeSet.has(u.id)));
    }
  };

  const removeGroup = (g: { kind: UnitKind; type: string }) =>
    onChange(value.filter((u) => !inGroup(u, g)));

  const pasteNumbers = (g: { kind: UnitKind; type: string }, text: string) => {
    const nums = text
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (nums.length === 0) return;
    // Заполняем единицы позиции по порядку (перезаписываем).
    let k = 0;
    onChange(
      value.map((u) => {
        if (!inGroup(u, g)) return u;
        const n = nums[k];
        k++;
        return n !== undefined ? { ...u, number: n } : u;
      }),
    );
  };

  return (
    <Stack gap="sm">
      <Group gap="xs">
        {kinds.map((k) => (
          <Button
            key={k}
            variant="default"
            leftSection={<IconPlus size={14} />}
            onClick={() => openAdd(k)}
          >
            Добавить {KIND_WORD[k].many}
          </Button>
        ))}
        {value.length === 0 && (
          <Text size="xs" c="dimmed">
            Единицы транспорта не добавлены. Номера можно вписать сейчас или позже.
          </Text>
        )}
      </Group>

      {groupsInOrder.map((g) => {
        const indices = value.map((u, i) => ({ u, i })).filter((x) => inGroup(x.u, g));
        const count = indices.length;
        const withNumbers = indices.filter((x) => x.u.number?.trim()).length;
        const typeLabel =
          typeOptionsFor(g.kind).find((o) => o.value === g.type)?.label ?? typeShort(g.type);
        return (
          <TypeGroup
            key={`${g.kind}|${g.type}`}
            type={g.type}
            label={`${KIND_WORD[g.kind].one === 'вагон' ? 'Вагон' : 'Контейнер'} · ${typeLabel}`}
            count={count}
            withNumbers={withNumbers}
            kindLabel={KIND_WORD[g.kind].one}
            ownershipOptions={ownershipOptionsFor(g.kind)}
            units={indices.map((x) => ({
              unit: x.u,
              placeholder: `${typeShort(g.type)} #${seqWithinType(value, x.i)}`,
              dup: Boolean(x.u.number?.trim()) && numberCounts[x.u.number.trim()] > 1,
            }))}
            onCount={(n) => setGroupCount(g, n)}
            onNumber={setNumber}
            onOwnership={setOwnership}
            onTypeOwnership={(o) => setGroupOwnership(g, o)}
            onRemoveUnit={removeUnit}
            onRemoveType={() => removeGroup(g)}
            onPaste={(text) => pasteNumbers(g, text)}
          />
        );
      })}

      <Modal
        opened={addFor !== null}
        onClose={() => setAddFor(null)}
        title={addFor ? `Добавить ${KIND_WORD[addFor].many}` : ''}
        size="md"
      >
        {addFor && (
          <Stack>
            <Select
              label={addFor === 'WAGON' ? 'Род вагона' : 'Тип контейнера'}
              value={addType || null}
              onChange={(v) => v && setAddType(v)}
              data={typeOptionsFor(addFor)}
              data-autofocus
            />
            <NumberInput
              label="Количество"
              value={addCount}
              onChange={setAddCount}
              min={1}
              allowDecimal={false}
            />
            <Select
              label="Принадлежность"
              value={addOwnership || null}
              onChange={(v) => setAddOwnership((v ?? '') as Ownership)}
              data={ownershipOptionsFor(addFor)}
              placeholder="не указана"
              clearable
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setAddFor(null)}>
                Отмена
              </Button>
              <Button onClick={confirmAdd}>Добавить</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

function TypeGroup({
  label,
  count,
  withNumbers,
  kindLabel,
  ownershipOptions,
  units,
  onCount,
  onNumber,
  onOwnership,
  onTypeOwnership,
  onRemoveUnit,
  onRemoveType,
  onPaste,
}: {
  type: string;
  label: string;
  count: number;
  withNumbers: number;
  kindLabel: string;
  ownershipOptions: { value: string; label: string }[];
  units: { unit: TransportUnitForm; placeholder: string; dup: boolean }[];
  onCount: (n: number) => void;
  onNumber: (id: string, number: string) => void;
  onOwnership: (id: string, ownership: Ownership) => void;
  onTypeOwnership: (ownership: Ownership) => void;
  onRemoveUnit: (id: string) => void;
  onRemoveType: () => void;
  onPaste: (text: string) => void;
}) {
  // Сводка по принадлежности для свёрнутого заголовка: «SOC 5 · COC 3».
  const ownSummary = ownershipOptions
    .map((o) => {
      const n = units.filter((x) => x.unit.ownership === o.value).length;
      return n ? `${ownershipShort(o.value as Ownership)} ${n}` : '';
    })
    .filter(Boolean)
    .join(' · ');
  // Принадлежность позиции: показываем её в шапке — общую, если у всех единиц
  // она одна. Раньше значение было видно только в раскрытом списке, а селект
  // «задать всем» сбрасывался после выбора — казалось, что выбор не сохранился.
  const ownValues = new Set(units.map((x) => x.unit.ownership || ''));
  const commonOwnership = ownValues.size === 1 ? ([...ownValues][0] as Ownership) : '';
  const mixedOwnership = ownValues.size > 1;
  const [open, setOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');

  return (
    <Card withBorder padding="sm">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs">
          <ActionIcon variant="subtle" color="gray" onClick={() => setOpen((o) => !o)}>
            {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
          <Text fw={500}>{label}</Text>
          <Badge variant="light">{count} шт</Badge>
          <Text size="xs" c="dimmed">
            {withNumbers}/{count} с номерами
          </Text>
          {mixedOwnership && ownSummary && (
            <Text size="xs" c="dimmed">
              {ownSummary}
            </Text>
          )}
        </Group>
        <Group gap="xs">
          <Select
            aria-label={`Принадлежность (${kindLabel}ы этого типа)`}
            value={commonOwnership || null}
            onChange={(v) => onTypeOwnership((v ?? '') as Ownership)}
            data={ownershipOptions}
            placeholder={mixedOwnership ? 'разная' : 'принадлежность'}
            clearable
            size="xs"
            w={170}
          />
          <NumberInput
            aria-label="Количество"
            value={count}
            onChange={(v) => onCount(Math.max(0, Number(v) || 0))}
            min={0}
            allowDecimal={false}
            w={90}
            size="xs"
          />
          <Tooltip label={`Удалить все ${kindLabel}ы этого типа`}>
            <ActionIcon color="red" variant="subtle" onClick={onRemoveType}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Collapse in={open}>
        <Stack gap={6} mt="sm">
          <Group justify="space-between" align="flex-end">
            <Text size="xs" c="dimmed">
              Принадлежность задаётся всей позиции в заголовке; ниже её можно уточнить по каждому
              {` ${kindLabel}у`}.
            </Text>
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => setPasteOpen((o) => !o)}
            >
              Вставить номера списком
            </Button>
          </Group>
          {pasteOpen && (
            <Stack gap={6}>
              <Textarea
                placeholder={'MSCU1234567\nTCLU7654321\n…'}
                autosize
                minRows={3}
                value={pasteText}
                onChange={(e) => setPasteText(e.currentTarget.value)}
              />
              <Group justify="flex-end" gap="xs">
                <Text size="xs" c="dimmed" style={{ marginRight: 'auto' }}>
                  По одному номеру на строку — заполнятся по порядку.
                </Text>
                <Button size="compact-xs" variant="default" onClick={() => setPasteOpen(false)}>
                  Отмена
                </Button>
                <Button
                  size="compact-xs"
                  onClick={() => {
                    onPaste(pasteText);
                    setPasteText('');
                    setPasteOpen(false);
                  }}
                >
                  Заполнить
                </Button>
              </Group>
            </Stack>
          )}
          {units.map(({ unit, placeholder, dup }, i) => (
            <Group key={unit.id} gap="xs" align="center" wrap="nowrap">
              <Text size="xs" c="dimmed" w={34} ta="right">
                #{i + 1}
              </Text>
              <TextInput
                placeholder={placeholder}
                value={unit.number}
                onChange={(e) => onNumber(unit.id, e.currentTarget.value)}
                error={dup ? 'Номер повторяется' : undefined}
                style={{ flex: 1 }}
              />
              <Select
                aria-label="Принадлежность"
                value={unit.ownership || null}
                onChange={(v) => onOwnership(unit.id, (v ?? '') as Ownership)}
                data={ownershipOptions}
                placeholder="—"
                clearable
                w={150}
              />
              <Tooltip label="Удалить">
                <ActionIcon color="red" variant="subtle" onClick={() => onRemoveUnit(unit.id)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          ))}
        </Stack>
      </Collapse>
    </Card>
  );
}

// =====================================================================
// Распределение груза по единицам (вкладки)
// =====================================================================

/** Группа груза по единицам (форма). Числовые поля — string|number для инпутов. */
export interface CargoGroupForm {
  unitIds: string[];
  cargoName: string;
  cargoWeightKg: number | string;
  cargoWeightNetKg: number | string;
  cargoVolumeM3: number | string;
  cargoUnits: number | string;
  cargoPackaging: string;
  cargoTnVed: string;
  cargoMarking: string;
  cargoIsDangerous: boolean;
  cargoAdrClass: string;
}

export function emptyCargoGroup(): CargoGroupForm {
  return {
    unitIds: [],
    cargoName: '',
    cargoWeightKg: '',
    cargoWeightNetKg: '',
    cargoVolumeM3: '',
    cargoUnits: '',
    cargoPackaging: '',
    cargoTnVed: '',
    cargoMarking: '',
    cargoIsDangerous: false,
    cargoAdrClass: '',
  };
}

export function CargoGroupEditor({
  units,
  value,
  onChange,
  kindLabelPlural = 'контейнерам',
}: {
  units: TransportUnitForm[];
  value: CargoGroupForm[];
  onChange: (next: CargoGroupForm[]) => void;
  /** Множественное число для подписи: «контейнерам» / «вагонам». */
  kindLabelPlural?: string;
}) {
  const [active, setActive] = useState('0');
  const options = unitOptions(units);
  const update = (i: number, patch: Partial<CargoGroupForm>) =>
    onChange(value.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));
  const add = () => {
    onChange([...value, emptyCargoGroup()]);
    setActive(String(value.length));
  };
  const remove = (i: number) => {
    onChange(value.filter((_, idx) => idx !== i));
    setActive('0');
  };

  return (
    <Stack gap="xs">
      <Text size="sm" c="dimmed">
        Разный груз по {kindLabelPlural} — заполните каждую группу и укажите её единицы.
      </Text>
      <Tabs value={active} onChange={(v) => setActive(v ?? '0')}>
        <Tabs.List>
          {value.map((g, i) => (
            <Tabs.Tab key={i} value={String(i)}>
              {g.cargoName || `Груз ${i + 1}`}
            </Tabs.Tab>
          ))}
          <Button variant="subtle" size="xs" leftSection={<IconPlus size={14} />} onClick={add}>
            Группа
          </Button>
        </Tabs.List>
        {value.map((g, i) => (
          <Tabs.Panel key={i} value={String(i)} pt="sm">
            <Stack gap="xs">
              <MultiSelect
                label="Единицы этой группы"
                data={options}
                value={g.unitIds}
                onChange={(v) => update(i, { unitIds: v })}
              />
              <TextInput
                label="Наименование груза"
                value={g.cargoName}
                onChange={(e) => update(i, { cargoName: e.currentTarget.value })}
              />
              <Group grow>
                <NumberInput
                  label="Кол-во мест"
                  value={g.cargoUnits}
                  onChange={(v) => update(i, { cargoUnits: v })}
                  min={0}
                  allowDecimal={false}
                />
                <TextInput
                  label="Упаковка"
                  value={g.cargoPackaging}
                  onChange={(e) => update(i, { cargoPackaging: e.currentTarget.value })}
                />
                <TextInput
                  label="ТН ВЭД"
                  value={g.cargoTnVed}
                  onChange={(e) => update(i, { cargoTnVed: e.currentTarget.value })}
                />
              </Group>
              <Group grow>
                <NumberInput
                  label="Вес брутто (с упаковкой), кг"
                  value={g.cargoWeightKg}
                  onChange={(v) => update(i, { cargoWeightKg: v })}
                  min={0}
                  decimalScale={2}
                />
                <NumberInput
                  label="Вес нетто (без упаковки), кг"
                  value={g.cargoWeightNetKg}
                  onChange={(v) => update(i, { cargoWeightNetKg: v })}
                  min={0}
                  decimalScale={2}
                />
                <NumberInput
                  label="Объём, м³"
                  value={g.cargoVolumeM3}
                  onChange={(v) => update(i, { cargoVolumeM3: v })}
                  min={0}
                  decimalScale={2}
                />
              </Group>
              <TextInput
                label="Маркировка"
                value={g.cargoMarking}
                onChange={(e) => update(i, { cargoMarking: e.currentTarget.value })}
              />
              <Checkbox
                label="Опасный груз"
                checked={g.cargoIsDangerous}
                onChange={(e) => update(i, { cargoIsDangerous: e.currentTarget.checked })}
              />
              {g.cargoIsDangerous && (
                <TextInput
                  label="Класс ДОПОГ"
                  value={g.cargoAdrClass}
                  onChange={(e) => update(i, { cargoAdrClass: e.currentTarget.value })}
                />
              )}
              {value.length > 1 && (
                <Group justify="flex-end">
                  <Button
                    color="red"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => remove(i)}
                  >
                    Удалить группу
                  </Button>
                </Group>
              )}
            </Stack>
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
}
